/**
 * Admin — users list (searchable, paginated). Click a user to edit their access.
 * Also opens the bulk "grant an event to many users" dialog. Admin-only (the API
 * enforces it too).
 */
import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  IconShieldLock,
  IconSearch,
  IconChevronRight,
  IconUsersGroup,
  IconMoodSad,
} from '@tabler/icons-react';
import { useAuthStore } from '../state/authStore.ts';
import { adminListUsers } from '../lib/apiClient.ts';
import { Spinner } from '../components/ui/Spinner.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { Button } from '../components/ui/Button.tsx';
import { BulkGrantDialog } from '../components/admin/BulkGrantDialog.tsx';
import { PendingRequests } from '../components/admin/PendingRequests.tsx';
import { useDebounced } from '../hooks/useDebounced.ts';

export function AdminPage() {
  const me = useAuthStore((s) => s.me);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounced(searchInput, 300);
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['admin-users', search],
      initialPageParam: null as string | null,
      queryFn: ({ pageParam, signal }) =>
        adminListUsers({ search, cursor: pageParam, limit: 50 }, signal),
      getNextPageParam: (last) => last.nextCursor,
    });

  if (me && me.role !== 'admin') return <Navigate to="/events" replace />;

  const users = (data?.pages ?? []).flatMap((p) => p.users);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <IconShieldLock className="h-6 w-6 text-accent" />
          <div>
            <h1 className="text-2xl font-bold text-fg">User access</h1>
            <p className="mt-0.5 text-sm text-fg-2">
              Manage who can view and upload to each event.
            </p>
          </div>
        </div>
        <Button variant="secondary" onClick={() => setBulkOpen(true)}>
          <IconUsersGroup className="h-4 w-4" /> Grant an event
        </Button>
      </div>

      <PendingRequests />

      {/* Search */}
      <div className="relative max-w-md">
        <IconSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by name or email…"
          aria-label="Search users"
          className="h-10 w-full rounded-xl border border-line bg-surface pl-9 pr-3 text-sm outline-none placeholder:text-muted"
        />
      </div>

      {isLoading && (
        <div className="flex justify-center py-16 text-accent">
          <Spinner className="h-7 w-7" />
        </div>
      )}
      {isError && <EmptyState icon={IconMoodSad} title="Couldn't load users" />}

      {!isLoading && !isError && users.length === 0 && (
        <EmptyState
          icon={IconShieldLock}
          title={search ? 'No matching users' : 'No users yet'}
          description={
            search
              ? 'Try a different search.'
              : "When someone signs in with Google, they'll appear here."
          }
        />
      )}

      {users.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
          <ul className="divide-y divide-line">
            {users.map((u) => (
              <li key={u.email}>
                <Link
                  to={`/admin/users/${encodeURIComponent(u.email)}`}
                  className="flex items-center gap-3 px-4 py-3 transition duration-200 ease-standard hover:bg-grid"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-fg">{u.name || u.email}</span>
                      {u.role === 'admin' && (
                        <span className="rounded-full bg-accent-bg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
                          Admin
                        </span>
                      )}
                    </div>
                    <div className="truncate text-xs text-muted">{u.email}</div>
                  </div>
                  <span className="shrink-0 text-xs text-fg-2">
                    {u.eventCount === 'all'
                      ? 'all events'
                      : `${u.eventCount} event${u.eventCount !== 1 ? 's' : ''}`}
                  </span>
                  <IconChevronRight className="h-4 w-4 shrink-0 text-muted" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasNextPage && (
        <div className="flex justify-center">
          <Button variant="secondary" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? <Spinner className="h-4 w-4" /> : 'Load more'}
          </Button>
        </div>
      )}

      <BulkGrantDialog open={bulkOpen} onClose={() => setBulkOpen(false)} />
    </div>
  );
}
